import { UIEvent, useMemo, useState } from "react";
import { type DebouncedFunc } from "lodash-es";
import debounce from "lodash-es/debounce";

type InfiniteListProps<T> = {
  items: T[];
  children: (
    items: T[],
    onScroll: DebouncedFunc<(e: UIEvent<HTMLDivElement>) => void>
  ) => React.ReactNode;
  screensGap?: number;
  itemsPerPage?: number;
};

const SCREENS_GAP_QUANTITY = 6;
const LIST_ITEMS_PER_PAGE = 100;

const InfiniteList = <T,>(props: InfiniteListProps<T>) => {
  const {
    items,
    children,
    screensGap = SCREENS_GAP_QUANTITY,
    itemsPerPage = LIST_ITEMS_PER_PAGE,
  } = props;

  const [count, setCount] = useState(1);
  const [previousItemsLength, setPreviousItemsLength] = useState(items.length);

  const itemsChanged = previousItemsLength !== items.length;

  if (itemsChanged) {
    setPreviousItemsLength(items.length);
    setCount(1);
  }

  const resolvedCount = itemsChanged ? 1 : count;
  const hasMore = items.length > itemsPerPage * resolvedCount;

  const handleLoadMore = debounce((e: UIEvent<HTMLDivElement>) => {
    if (hasMore) {
      const target = e.target as HTMLDivElement;
      const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight * screensGap;

      if (isBottom) {
        setCount(resolvedCount + 1);
      }
    }
  }, 20);

  const infiniteItems = useMemo(() => {
    return items.slice(0, itemsPerPage * resolvedCount);
  }, [items, itemsPerPage, resolvedCount]);

  return <>{children(infiniteItems, handleLoadMore)}</>;
};

export default InfiniteList;
